/**
 * Why a presenter-photo request could not be honoured, in the user's terms
 * (pure). One map for uploads, retries and corrections, because the three
 * share most codes; `tagErrorMessage` also reads the `tagError` an upload
 * answers with when the photo landed but its description did not.
 */
import {
  PRESENTER_PHOTO_MAX_COUNT,
  PRESENTER_PHOTO_SUMMARY_MAX_LENGTH,
  type PresenterPhotoTagError,
} from "@kola/shared";

import { ApiError } from "../../core/api.js";

/** The reason a tagging pass produced nothing, as a sentence. */
export function tagErrorMessage(code: PresenterPhotoTagError | string): string {
  switch (code) {
    case "ai-not-configured":
      return "AI is not configured on this server yet (no OpenRouter key) — the photo is kept, not described.";
    case "photo-unreadable":
      return "The photo's bytes could not be read back. Upload it again.";
    case "unusable-response":
      return "The model did not describe the photo. Try again, or have an admin pick a model that takes image input.";
    case "upstream-model-rejected":
      return "That model would not take the photo — the tagging model must accept image input.";
    case "upstream-timeout":
      return "The model took too long. Try again.";
    case "upstream-unauthorized":
      return "The AI provider rejected this server's key.";
    case "upstream-payment-required":
      return "The AI provider account is out of credit.";
    case "upstream-rate-limited":
      return "The AI provider is rate-limiting this server. Give it a minute.";
    case "upstream-unreachable":
      return "Couldn't reach the AI provider.";
    default:
      return "The photo could not be described. Try again.";
  }
}

export function presenterPhotoErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "unsupported-media-type":
        return "That file is not a supported image (png/jpeg/gif/webp).";
      case "too-many-photos":
        return `The library holds up to ${PRESENTER_PHOTO_MAX_COUNT} photos.`;
      case "not-tagged":
        return "The photo has no description yet — tag it first.";
      case "invalid-summary":
        return `A description is one line of up to ${PRESENTER_PHOTO_SUMMARY_MAX_LENGTH} characters.`;
      case "invalid-model":
        return "That model id doesn't look right. Pick one from the list.";
      case "unknown-photo":
        return "That photo is not in the library.";
      case "forbidden":
        return "Your account can't change the presenter photos.";
      case "":
        break;
      default:
        if (error.status === 413) return "That image is too large (20 MB max).";
        return tagErrorMessage(error.code);
    }
    return `The request failed (${error.status}). Try again.`;
  }
  return "The request failed — check your connection and try again.";
}
